import React, { useState } from 'react';
import ContactForm from './dialogs/contactFormDialog';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import EmailIcon from '@mui/icons-material/Email';
import TranslateIcon from '@mui/icons-material/Translate';
import GitHubIcon from '@mui/icons-material/GitHub';
import Dialog from "@mui/material/Dialog";
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import SlideUpTransition from './dialogs/SlideUpTransition';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { MenuItem, ClickAwayListener, Paper } from '@mui/material';

function SideBar() {
  const { t, i18n } = useTranslation();
  const isCompactScreen = useMediaQuery('(max-width: 768px)');
  const [isContactFormOpen, setContactFormOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  // Pixel font for the bottom bar labels
  const labelFont = '"Press Start 2P", cursive';
  const labelFontSize = isCompactScreen ? 9 : 10;
  const labelLineHeight = 1.6;

  const handleOpenContactForm = () => {
    setContactFormOpen(true);
  };

  const handleCloseContactForm = () => {
    setContactFormOpen(false);
  };

  const handleOpenWebpage = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const changeLanguageHandler = (lang: string) => {
    i18n.changeLanguage(lang);
    setLangMenuOpen(false);
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: isCompactScreen ? 0 : 'auto',
          bottom: isCompactScreen ? 'auto' : 0,
          left: 0,
          width: '100%',
          backgroundColor: '#6F6F78',
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: isCompactScreen ? '8px 10px' : '12px 18px',
          alignItems: 'center',
          padding: isCompactScreen
            ? 'calc(8px + env(safe-area-inset-top)) 10px 8px'
            : '10px 16px calc(10px + env(safe-area-inset-bottom))',
          color: 'white',
          zIndex: 1000,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', whiteSpace: isCompactScreen ? 'normal' : 'nowrap', textAlign: 'center', minWidth: isCompactScreen ? 'calc(50% - 10px)' : 'auto', fontSize: labelFontSize, fontFamily: labelFont, lineHeight: labelLineHeight }} onClick={() => setLangMenuOpen(!langMenuOpen)}>
          <TranslateIcon style={{ marginRight: 6 }} />
          {t('sidebar.languages') || 'Languages'}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', whiteSpace: isCompactScreen ? 'normal' : 'nowrap', textAlign: 'center', minWidth: isCompactScreen ? 'calc(50% - 10px)' : 'auto', fontSize: labelFontSize, fontFamily: labelFont, lineHeight: labelLineHeight }} onClick={handleOpenContactForm}>
          <EmailIcon style={{ marginRight: 6 }} />
          {t('sidebar.contactMe')}
        </div>

        <a
          href="mailto:fabian.ebert@online.de"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', color: 'inherit', whiteSpace: isCompactScreen ? 'normal' : 'nowrap', textAlign: 'center', minWidth: isCompactScreen ? 'calc(50% - 10px)' : 'auto', fontSize: labelFontSize, fontFamily: labelFont, lineHeight: labelLineHeight }}
        >
          <AlternateEmailIcon style={{ marginRight: 6 }} />
          {t('sidebar.directEmail')}
        </a>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', whiteSpace: isCompactScreen ? 'normal' : 'nowrap', textAlign: 'center', minWidth: isCompactScreen ? 'calc(50% - 10px)' : 'auto', fontSize: labelFontSize, fontFamily: labelFont, lineHeight: labelLineHeight }} onClick={() => handleOpenWebpage('https://github.com/SirArchy')}>
          <GitHubIcon style={{ marginRight: 6 }} />
          {t('sidebar.github')}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', whiteSpace: isCompactScreen ? 'normal' : 'nowrap', textAlign: 'center', minWidth: isCompactScreen ? 'calc(50% - 10px)' : 'auto', fontSize: labelFontSize, fontFamily: labelFont, lineHeight: labelLineHeight }} onClick={() => handleOpenWebpage('https://www.linkedin.com/in/fabian-e-762b85244')}>
          <LinkedInIcon style={{ marginRight: 6 }} />
          {t('sidebar.linkedin')}
        </div>
      </div>

      {/* Language Dropdown */}
      {langMenuOpen && (
        <ClickAwayListener onClickAway={() => setLangMenuOpen(false)}>
          <Paper
            style={{
              position: 'fixed',
              top: isCompactScreen ? 'calc(56px + env(safe-area-inset-top))' : 'auto',
              bottom: isCompactScreen ? 'auto' : 60,
              left: 10,
              backgroundColor: '#6F6F78',
              color: 'white',
              padding: '8px 0',
              zIndex: 1100,
            }}
          >
            <MenuItem onClick={() => changeLanguageHandler("en")}>English</MenuItem>
            <MenuItem onClick={() => changeLanguageHandler("de")}>Deutsch</MenuItem>
          </Paper>
        </ClickAwayListener>
      )}

      {/* Contact Form Dialog */}
      <Dialog
        open={isContactFormOpen}
        onClose={handleCloseContactForm}
        fullScreen={isCompactScreen}
        TransitionComponent={SlideUpTransition}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: isCompactScreen ? 0 : '22px',
            margin: isCompactScreen ? 0 : 2,
            width: isCompactScreen ? '100vw' : 'auto',
            maxWidth: isCompactScreen ? '100vw' : 'min(92vw, 720px)',
            maxHeight: isCompactScreen ? '100dvh' : '90dvh',
            overflow: 'hidden',
          },
        }}
      >
        <IconButton
          aria-label="Close dialog"
          onClick={handleCloseContactForm}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 20,
            color: '#fff',
            backgroundColor: 'rgba(0,0,0,0.45)',
            border: '1px solid rgba(255,255,255,0.2)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.62)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <ContactForm />
      </Dialog>
    </>
  );
}

export default SideBar;
